import { cx } from "../../lib/cx.js";
import { VisuallyHidden } from "../VisuallyHidden/VisuallyHidden.js";
import { mediaQueries } from "../../tokens/breakpoints.js";
import { useMediaQuery } from "../../lib/useMediaQuery.js";
import { addMonths, isBeforeDay, startOfDay, startOfMonth } from "../Calendar/dates.js";
import { Calendar } from "../Calendar/Calendar.js";
import { useFormFieldControl } from "../FormField/FormFieldContext.js";
import { DateField } from "../_dateField/DateField.js";
import { parseTypedRange } from "../_dateField/typed.js";
import { TYPED_FORMAT, formatPickerDate } from "../DatePicker/DatePicker.js";
import { isSameRange, resolvePreset } from "./presets.js";
/* empty css                    */
import { forwardRef, useId, useState } from "react";
import { Fragment as Fragment$1, jsx, jsxs } from "react/jsx-runtime";
//#region src/components/DateRangePicker/DateRangePicker.tsx
var EMPTY = {
	start: null,
	end: null
};
/**
* A date-range field: a button showing the range that opens two months side by side.
* The first pick sets the start; the second sets the end (the range previews to the day
* under the pointer or focus) and closes it. The ref goes to the button.
*/
var DateRangePicker = forwardRef(function DateRangePicker({ value, defaultValue = EMPTY, onChange, allowTextInput = false, startPlaceholder = allowTextInput ? TYPED_FORMAT : "Start date", endPlaceholder = allowTextInput ? TYPED_FORMAT : "End date", min = null, max = null, disabled = false, size = "md", format = formatPickerDate, months = 2, presets, clearable = true, invalid: invalidProp, id: idProp, "aria-label": ariaLabel, "aria-describedby": describedByProp, today, defaultOpen = false, className }, ref) {
	const generatedId = useId();
	const { id = `ds-daterangepicker-${generatedId}`, "aria-describedby": describedBy, invalid } = useFormFieldControl({
		id: idProp,
		"aria-describedby": describedByProp,
		invalid: invalidProp
	});
	const [internal, setInternal] = useState(defaultValue);
	const range = value ?? internal;
	const commit = (next) => {
		if (value === void 0) setInternal(next);
		onChange?.(next);
	};
	const narrow = useMediaQuery(mediaQueries.below.sm);
	const shown = narrow ? 1 : Math.max(1, Math.floor(months));
	const [open, setOpen] = useState(defaultOpen);
	const [month, setMonth] = useState(() => startOfMonth(range.start ?? today ?? /* @__PURE__ */ new Date()));
	const [hovered, setHovered] = useState(null);
	const pick = (day) => {
		const picked = startOfDay(day);
		if (!range.start || range.end) {
			commit({
				start: picked,
				end: null
			});
			return;
		}
		commit(isBeforeDay(picked, range.start) ? {
			start: picked,
			end: range.start
		} : {
			start: range.start,
			end: picked
		});
		setHovered(null);
		setOpen(false);
	};
	const choices = presets?.map((preset) => ({
		label: preset.label,
		range: resolvePreset(preset, startOfDay(today ?? /* @__PURE__ */ new Date()), min, max)
	}));
	const choose = (chosen) => {
		commit(chosen);
		setHovered(null);
		setOpen(false);
	};
	const startText = range.start ? format(range.start) : startPlaceholder;
	const endText = range.end ? format(range.end) : endPlaceholder;
	const segment = (date, text) => date ? text : /* @__PURE__ */ jsx("span", {
		className: "ds-datepicker__placeholder",
		children: text
	});
	const empty = !range.start && !range.end;
	const rangeText = /* @__PURE__ */ jsxs(Fragment$1, { children: [
		segment(range.start, startText),
		" ",
		/* @__PURE__ */ jsx("span", {
			"aria-hidden": "true",
			className: empty ? "ds-datepicker__placeholder" : void 0,
			children: "–"
		}),
		/* @__PURE__ */ jsx(VisuallyHidden, { children: "to" }),
		" ",
		segment(range.end, endText)
	] });
	return /* @__PURE__ */ jsx(DateField, {
		id,
		size,
		invalid,
		disabled,
		valueText: rangeText,
		describedBy,
		"aria-label": ariaLabel,
		clearLabel: clearable && !empty ? `Clear range, ${startText} to ${endText}` : null,
		onClear: () => commit(EMPTY),
		open,
		onOpenChange: (next) => {
			if (next) setMonth(startOfMonth(range.start ?? today ?? /* @__PURE__ */ new Date()));
			else setHovered(null);
			setOpen(next);
		},
		popoverLabel: range.start && !range.end ? "Choose the end date" : "Choose a date range",
		triggerRef: ref,
		className,
		typed: allowTextInput ? {
			text: empty ? "" : `${range.start ? format(range.start) : ""} – ${range.end ? format(range.end) : ""}`.trim(),
			onCommit: (text) => {
				const typed = parseTypedRange(text);
				if (!typed) return false;
				const outside = (day) => !!day && (!!min && isBeforeDay(day, min) || !!max && isBeforeDay(max, day));
				if (outside(typed.start) || outside(typed.end)) return false;
				if (!typed.start && !clearable) return true;
				if (!isSameRange(typed, range)) commit(typed);
				return true;
			},
			placeholder: `${startPlaceholder} – ${endPlaceholder}`,
			hint: `Format ${TYPED_FORMAT} – ${TYPED_FORMAT}`,
			calendarLabel: "Choose dates"
		} : void 0,
		children: /* @__PURE__ */ jsxs("div", {
			className: cx(choices && "ds-daterangepicker__body", choices && narrow && "ds-daterangepicker__body--stacked"),
			children: [choices && /* @__PURE__ */ jsx("div", {
				role: "group",
				"aria-label": "Presets",
				className: "ds-daterangepicker__presets",
				children: choices.map(({ label, range: chosen }) => /* @__PURE__ */ jsx("button", {
					type: "button",
					className: "ds-daterangepicker__preset",
					"aria-pressed": chosen !== null && isSameRange(range, chosen),
					disabled: chosen === null,
					onClick: () => chosen && choose(chosen),
					children: label
				}, label))
			}), /* @__PURE__ */ jsx("div", {
				className: "ds-daterangepicker__months",
				children: Array.from({ length: shown }, (_, index) => /* @__PURE__ */ jsx(Calendar, {
					month: addMonths(month, index),
					onMonthChange: (next) => setMonth(addMonths(next, -index)),
					rangeStart: range.start,
					rangeEnd: range.end,
					hovered: range.start && !range.end ? hovered : null,
					onHover: setHovered,
					min,
					max,
					navPrev: index === 0,
					navNext: index === shown - 1,
					showOutsideDays: shown === 1,
					...today && { today },
					onSelect: pick
				}, index))
			})]
		})
	});
});
//#endregion
export { DateRangePicker };

//# sourceMappingURL=DateRangePicker.js.map